import { QrChannel, QrChannelSummary } from "../../types/qr-analytics.type";

/**
 * The payment channels, defined once so the tiles, the stacked bars and the branch table
 * can never drift apart on colour, label or order.
 *
 * ORDER IS FIXED and never cycled: เงินสด, เงินโอน, QR. It is the stacking order (cash at
 * the base, QR on top where the eye lands) and the legend order, and a filter that removes
 * a channel must not repaint the survivors.
 *
 * The three hues were validated as a categorical set against the white card surface —
 * lightness band, chroma floor, colour-vision separation on every pair, and 3:1 contrast
 * all pass. They are NOT free to adjust by eye: the obvious green/amber pairing collapses
 * to ΔE 2.1 under protanopia, which is why cash is blue rather than the usual green.
 * QR keeps the brand amber, darkened to the step that clears the band.
 */
export const QR_CHANNELS: {
  key: QrChannel;
  label: string;
  color: string;
  countKey: keyof QrChannelSummary;
  percentKey: keyof QrChannelSummary;
  amountKey: keyof QrChannelSummary;
}[] = [
  {
    key: "cash",
    label: "เงินสด",
    color: "#1565C0",
    countKey: "cash_count",
    percentKey: "cash_percent",
    amountKey: "cash_amount",
  },
  {
    key: "transfer",
    label: "เงินโอน",
    color: "#AD1457",
    countKey: "transfer_count",
    percentKey: "transfer_percent",
    amountKey: "transfer_amount",
  },
  {
    key: "qr",
    label: "QR",
    color: "#B26A00",
    countKey: "qr_count",
    percentKey: "qr_percent",
    amountKey: "qr_amount",
  },
];

/**
 * เงินโอน+QR — the group the owner reads against เงินสด, and the one this screen headlines.
 *
 * It is a GROUP, not a fourth series, so it deliberately has no hue of its own: its members
 * keep their colours, and the group is drawn with structure instead — a bracket over the
 * share bar, a framed tile, the line on top of the trend's stacked areas. A new categorical
 * hue would have had to clear the same colour-vision checks as the three above, and the
 * nearest candidates collapse onto cash blue. The charcoal is the platform's brand ink.
 */
export const DIGITAL_GROUP = {
  label: "เงินโอน+QR",
  color: "#363A44",
};

/** The group's members, in the fixed channel order, for the breakdowns under the group. */
export const DIGITAL_CHANNELS = QR_CHANNELS.filter(
  (channel) => channel.key === "transfer" || channel.key === "qr",
);

export const CASH_CHANNEL = QR_CHANNELS[0];

/**
 * เงินเชื่อ is not a fourth series.
 *
 * A credit bill belongs on an invoice, not a cash sale, so this bucket should be empty —
 * it is carried so a stray row is visible rather than silently folded into เงินสด, and it
 * renders in recessive grey with its own label only when the count is non-zero. Giving it
 * a fourth categorical hue would have broken the validated separation of the real three.
 */
export const CREDIT_CHANNEL = {
  key: "credit" as const,
  label: "เงินเชื่อ",
  color: "#9E9E9E",
};

/** The neutral ink every value and label wears — identity is carried by the mark, not the text. */
export const CHART_INK = "#5A6270";

/** Grid and axis lines stay recessive; they orient, they do not compete with the bars. */
export const CHART_GRID = "#E4E6EB";

/**
 * A bucket key as the axis should read it: "YYYY-MM-DD" → "5 ก.ย.", "YYYY-MM" → "ก.ย. 69".
 * Kept here rather than in the charts so the trend line and the channel bars label the same
 * bucket identically.
 */
export const formatBucketLabel = (bucket: string): string => {
  const THAI_MONTHS = [
    "ม.ค.",
    "ก.พ.",
    "มี.ค.",
    "เม.ย.",
    "พ.ค.",
    "มิ.ย.",
    "ก.ค.",
    "ส.ค.",
    "ก.ย.",
    "ต.ค.",
    "พ.ย.",
    "ธ.ค.",
  ];
  const parts = bucket.split("-");
  const month = THAI_MONTHS[Number(parts[1]) - 1] ?? parts[1];
  if (parts.length === 2) {
    // Buddhist-era two-digit year, matching every other date on the platform's reports.
    return `${month} ${String((Number(parts[0]) + 543) % 100).padStart(2, "0")}`;
  }
  return `${Number(parts[2])} ${month}`;
};
